/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
/**
 * useTransmittalReferences
 *
 * Platform-agnostic hook for managing transmittal file references state.
 * Handles adding, removing, and updating remarks for attached files.
 *
 * Usage:
 * - Web: Used in CreateTransmittal modal with SlickGrid
 * - Mobile: Used with FlatList or similar
 */

import { useState, useCallback, useMemo } from "react";
import {
  TransmittalReference,
  ReferenceDatasetItem,
  AttachedResourcePayload,
  ForwardedResourcePayload,
  RESOURCE_TYPE_FILE,
  RESOURCE_TYPE_MERGED_FILE,
} from "./transmittal.types";

interface UseTransmittalReferencesParams {
  initialReferences?: TransmittalReference[];
}

interface UseTransmittalReferencesReturn {
  // State
  references: Record<string, TransmittalReference>;
  referencesDataset: ReferenceDatasetItem[];
  alreadyAttachedIds: string[];

  // Actions
  addReferences: (refs: TransmittalReference[]) => void;
  removeReferences: (ids: string[]) => void;
  updateRemark: (id: string, remark: string) => void;
  setReferences: (refs: Record<string, TransmittalReference>) => void;
  clear: () => void;

  // Derived
  hasReferences: boolean;
  referenceCount: number;

  // Transformers (for API payload)
  getAttachedResourcesPayload: () => AttachedResourcePayload[];
  getForwardedResourcesPayload: () => ForwardedResourcePayload[];
}

function useTransmittalReferences({
  initialReferences = [],
}: UseTransmittalReferencesParams = {}): UseTransmittalReferencesReturn {
  // Build initial state from initialReferences
  const buildInitialState = useCallback(() => {
    const refs: Record<string, TransmittalReference> = {};
    initialReferences.forEach((ref) => {
      if (ref.fileVersion) {
        refs[ref.fileVersion] = { ...ref };
      }
    });
    return refs;
  }, []);

  const [references, setReferencesState] = useState<Record<string, TransmittalReference>>(buildInitialState);

  // Derived: dataset for grids/lists
  const referencesDataset = useMemo<ReferenceDatasetItem[]>(() => {
    return Object.values(references).map((ref) => ({
      id: ref.fileVersion,
      name: ref.name,
      versionNumber: ref.versionNumber,
      size: ref.size,
      dateCreated: ref.dateCreated,
      resourceType: ref.resourceType,
      fileType: ref.fileType,
      remarks: ref.remarks || "",
    }));
  }, [references]);

  // Derived: already attached IDs (for disabling in file picker)
  const alreadyAttachedIds = useMemo<string[]>(() => {
    return Object.keys(references);
  }, [references]);

  // Derived: boolean checks
  const hasReferences = useMemo(() => Object.keys(references).length > 0, [references]);
  const referenceCount = useMemo(() => Object.keys(references).length, [references]);

  // Action: Add references (merges with existing, preserves remarks)
  const addReferences = useCallback((newRefs: TransmittalReference[]) => {
    setReferencesState((prev) => {
      const updated = { ...prev };
      newRefs.forEach((ref) => {
        if (ref.fileVersion) {
          updated[ref.fileVersion] = {
            ...ref,
            remarks: updated[ref.fileVersion]?.remarks || ref.remarks || "",
          };
        }
      });
      return updated;
    });
  }, []);

  // Action: Remove references by IDs
  const removeReferences = useCallback((ids: string[]) => {
    setReferencesState((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => {
        delete updated[id];
      });
      return updated;
    });
  }, []);

  // Action: Update remark for a specific reference
  const updateRemark = useCallback((id: string, remark: string) => {
    setReferencesState((prev) => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: {
          ...prev[id],
          remarks: remark,
        },
      };
    });
  }, []);

  // Action: Set references directly (for bulk operations)
  const setReferences = useCallback((refs: Record<string, TransmittalReference>) => {
    setReferencesState(refs);
  }, []);

  // Action: Clear all references
  const clear = useCallback(() => {
    setReferencesState({});
  }, []);

  // Transformer: Get attached resources payload for API
  const getAttachedResourcesPayload = useCallback((): AttachedResourcePayload[] => {
    const payload: AttachedResourcePayload[] = [];
    Object.values(references).forEach((ref) => {
      if ([RESOURCE_TYPE_FILE, RESOURCE_TYPE_MERGED_FILE].includes(ref.resourceType)) {
        payload.push({
          fileVersionId: ref.fileVersion,
          remarks: ref.remarks || "",
        });
      }
    });
    return payload;
  }, [references]);

  // Transformer: Get forwarded resources payload for API
  const getForwardedResourcesPayload = useCallback((): ForwardedResourcePayload[] => {
    const payload: ForwardedResourcePayload[] = [];
    Object.values(references).forEach((ref) => {
      if (ref.transmittalVersionId) {
        payload.push({
          resourceType: ref.resourceType,
          resourceId: ref.fileVersion,
          transmittalId: ref.transmittalVersionId,
          remarks: ref.remarks || "",
        });
      }
    });
    return payload;
  }, [references]);

  return {
    // State
    references,
    referencesDataset,
    alreadyAttachedIds,

    // Actions
    addReferences,
    removeReferences,
    updateRemark,
    setReferences,
    clear,

    // Derived
    hasReferences,
    referenceCount,

    // Transformers
    getAttachedResourcesPayload,
    getForwardedResourcesPayload,
  };
}

export default useTransmittalReferences;
